<template>
  <div :style="{ minHeight: `${minHeight}px`, padding: '16px' }">
    <Form inline>
      <FormItem
        label="Column"
        class="stat-form-item"
      >
        <Select
          v-model="selectedColumns"
          multiple
        >
          <Option
            v-for="column in columns"
            :key="column.name"
            :value="column.name"
            :label="column.name"
          />
        </Select>
      </FormItem>
      <FormItem
        label="Statistic"
        class="stat-form-item"
      >
        <Select
          v-model="selectedStats"
          multiple
        >
          <Option
            v-for="statColumn in statTableColumns.filter(statColumn => statColumn.key !== 'name')"
            :key="statColumn.key"
            :value="statColumn.key"
            :label="statColumn.title"
          />
        </Select>
      </FormItem>
    </Form>
    <Table
      :columns="filteredStatTableColumns"
      :data="filteredStatTableData"
      :loading="loading"
      border
    />
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    statementId: {
      type: String,
      default: ''
    },
    columnTypeObj: {
      type: Object,
      default: () => null
    },
    minHeight: {
      type: Number,
      default: 500
    }
  },

  data () {
    return {
      selectedColumns: [],
      selectedStats: [],
      statTableColumns: [
        {
          title: 'DISTINCT COUNT',
          key: 'count'
        },
        {
          title: 'Field Name',
          key: 'name',
          maxWidth: 250,
          fixed: 'left'
        },
        {
          title: 'SUM',
          key: 'sum'
        },
        {
          title: 'AVERAGE',
          key: 'average'
        },
        {
          title: 'MIN',
          key: 'min'
        },
        {
          title: 'MAX',
          key: 'max'
        }
      ],
      statTableData: [],
      loading: true
    }
  },

  computed: {
    columns () {
      if (!this.columnTypeObj) {
        return []
      }

      return Object.keys(this.columnTypeObj).map(columnName => ({
        name: columnName,
        type: this.columnTypeObj[columnName]
      }))
    },

    filteredStatTableData () {
      if (!this.selectedColumns.length) {
        return this.statTableData
      }

      return this.statTableData.filter(statRow => this.selectedColumns.includes(statRow.name))
    },

    filteredStatTableColumns () {
      if (!this.selectedStats.length) {
        return this.statTableColumns
      }

      return this.statTableColumns.filter(statCol => this.selectedStats.includes(statCol.key) || statCol.key === 'name')
    }
  },

  watch: {
    columns () {
      this.setStatTableData()
    }
  },

  mounted () {
    this.setStatTableData()
  },

  methods: {
    /**
     * Sets the query statistics table data
     */
    async setStatTableData () {
      this.loading = true
      const tableStats = await this.getColumnStats()
      this.statTableData = tableStats
      this.loading = false
    },

    /**
     * Creates a query string that is used to get the statistics for the a given column.
     * The query will return the distinct count, sum, average, min, and max for a column.
     * If a column is not a number type it will only return distinct count
     * @param {Object} column - The column object in which we are creating a stats query for
     * @return {String}
     */
    createColumnStatQuery (column) {
      const { name, type } = column
      let statQuery = `
        SELECT '${name}' as name, COUNT(DISTINCT "${name}") as count
      `

      if (type === 'fixed') {
        statQuery += `
          , SUM("${name}") as sum, AVG("${name}") as average, MIN("${name}") as min, MAX("${name}") as max
        `
      } else {
        statQuery += `
          , null as sum, null as average, null as min, null as max
        `
      }

      statQuery += `
        FROM (
          select * from
          table(result_scan('${this.statementId}'))
        )
      `

      return statQuery
    },

    /**
     * Creates a single query used to get the statistics for all of the columns
     * @return {String}
     */
    createStatQuery () {
      return this.columns.map(column => this.createColumnStatQuery(column)).join('\nUNION ALL\n')
    },

    /**
     * Makes an API call to get the statistics for a given column
     * @param {Object} column
     * @return {Promise} - resolves into the statistics for the column
     */
    async getColumnStats () {
      if (!this.columns.length) {
        return Promise.resolve([])
      }

      try {
        const statQuery = this.createStatQuery()
        const result = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/snowflake/queries`,
          timeout: 1000 * 60 * 5,
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            query: statQuery
          }
        })).data

        if (result.error) {
          throw new Error(`Error while fetching stats: ${result.error.message}`)
        }

        return result.data.map(stat => ({
          name: stat['NAME'],
          count: stat['COUNT'],
          sum: stat['SUM'],
          average: stat['AVERAGE'],
          min: stat['MIN'],
          max: stat['MAX']
        }))
      } catch (error) {
        console.error(error.message)
        throw error
      }
    }
  }
}
</script>

<style scoped>
  .stat-form-item {
    width: 30%;
  }

  .stat-form-item >>> .ivu-form-item-label {
    font-weight: bold;
  }
</style>
